import { subMinutes } from 'date-fns'
import { useMemo } from 'react'

const DEFAULT_WINDOW_IN_MINUTES = 15
const DEFAULT_STEP_IN_SECONDS = 300

export type TrailingUsageWindow = {
  from: string
  to: string
  stepSeconds: number
}

export type TrailingUsageWindowParams = {
  windowInMinutes?: number
  stepSeconds?: number
}

// The upper bound is snapped down to a whole `stepSeconds` bucket so this stays
// a pure function of the current bucket rather than of the exact call time.
// That matters for `useSuspenseQuery`: a component that suspends before it has
// ever mounted loses its hook state and re-renders from scratch, so a raw
// `new Date()` here would produce fresh variables on every retry and refetch
// forever. Snapping makes every retry within the same bucket byte-identical.
export const getTrailingUsageWindow = ({
  windowInMinutes = DEFAULT_WINDOW_IN_MINUTES,
  stepSeconds = DEFAULT_STEP_IN_SECONDS,
}: TrailingUsageWindowParams = {}): TrailingUsageWindow => {
  const bucketInMilliseconds = stepSeconds * 1000
  const to = new Date(
    Math.floor(Date.now() / bucketInMilliseconds) * bucketInMilliseconds
  )
  return {
    from: subMinutes(to, windowInMinutes).toISOString(),
    to: to.toISOString(),
    stepSeconds,
  }
}

// Holds the window steady for as long as the component stays mounted, so
// crossing a bucket boundary cannot re-suspend a table that is already on
// screen. Remounting (navigation, company switch) picks up a fresh window.
export const useTrailingUsageWindow = (
  params: TrailingUsageWindowParams = {}
): TrailingUsageWindow => {
  const { windowInMinutes, stepSeconds } = params
  return useMemo(
    () => getTrailingUsageWindow({ windowInMinutes, stepSeconds }),
    [windowInMinutes, stepSeconds]
  )
}
